import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { adminStyles } from '../../styles/admin';
import { trackPageView } from '../../utils/tracking';
import { AdminService } from '../../services/adminService';
import { SalesData, QRScan } from '../../types/analytics';
import DashboardOverview from './DashboardOverview';
import MenuManagement from '../../pages/MenuManagement';
import QRCodeGenerator from '../QRCodeGenerator';
import { getTotalUniqueVisitors } from '../../services/qrTrackingService';

type Tab = 'overview' | 'qrcodes' | 'menu';

/**
 * The tab is derived from the URL (/admin/dashboard, /admin/dashboard/qrcodes,
 * /admin/dashboard/menu) so deep links, refresh and browser back/forward all
 * land on the right tab instead of always resetting to Overview.
 */
const tabFromPath = (pathname: string): Tab => {
  const segment = pathname.replace(/^\/admin\/dashboard\/?/, '').split('/')[0];
  return segment === 'qrcodes' || segment === 'menu' ? segment : 'overview';
};

const Dashboard: React.FC = () => {
  const [salesData, setSalesData] = useState<SalesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [qrStats, setQrStats] = useState<QRScan[]>([]);
  const [uniqueVisitors24h, setUniqueVisitors24h] = useState<number | null>(null);
  const notificationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const activeTab = tabFromPath(location.pathname);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      // Sales/orders/QR/popular items are aggregated over a 7-day window;
      // the overview labels must say so (see DashboardOverview).
      const timeframe = {
        start: new Date(new Date().setDate(new Date().getDate() - 7)),
        end: new Date()
      };
      const [data, visitors] = await Promise.all([
        AdminService.getDashboardData(timeframe),
        // Real 24h unique visitors. adminService's own
        // getUniqueVisitors('24') call treats '24' as a table number and
        // always returns ~0 (reported to the service owner).
        getTotalUniqueVisitors(24)
      ]);
      setSalesData(data);
      setUniqueVisitors24h(visitors);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQRStats = useCallback(async () => {
    try {
      const timeframe = {
        start: new Date(new Date().setDate(new Date().getDate() - 7)),
        end: new Date()
      };
      const stats = await AdminService.getQRScanStats(timeframe);
      setQrStats(stats);
    } catch (err) {
      console.error('Error loading QR stats:', err);
      setError('Failed to load QR statistics');
    }
  }, []);

  useEffect(() => {
    trackPageView('admin_dashboard');
    loadDashboardData();
  }, [loadDashboardData]);

  // A failed fetch must not poison every tab: errors are shown as a banner
  // next to the content (below) and cleared whenever the tab changes.
  useEffect(() => {
    setError(null);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'qrcodes') {
      loadQRStats();
    }
  }, [activeTab, loadQRStats]);

  useEffect(() => {
    return () => {
      if (notificationTimer.current) {
        clearTimeout(notificationTimer.current);
      }
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Legacy cleanup: older builds wrote an `adminAuth` flag here.
      localStorage.removeItem('adminAuth');
      navigate('/admin');
    } catch (err) {
      console.error('Error signing out:', err);
      showNotification('error', 'Failed to sign out');
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    if (notificationTimer.current) {
      clearTimeout(notificationTimer.current);
    }
    notificationTimer.current = setTimeout(() => setNotification(null), 3000);
  };

  const goToTab = (tab: Tab) => {
    navigate(tab === 'overview' ? '/admin/dashboard' : `/admin/dashboard/${tab}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <DashboardOverview
            salesData={salesData}
            loading={loading}
            uniqueVisitors24h={uniqueVisitors24h}
          />
        );
      case 'qrcodes':
        return (
          <div className="space-y-8">
            <QRCodeGenerator onQRGenerated={loadQRStats} />
            {qrStats.length > 0 && (
              <div className="mt-4 grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {qrStats.map((stat) => (
                  <div key={stat.tableId} className="p-4 sm:p-5 bg-white rounded-2xl border border-stone-200/70 shadow-sm">
                    <h3 className="font-bold text-base mb-2 text-stone-800">Table #{stat.tableId}</h3>
                    <div className="space-y-1.5">
                      <p className="text-sm text-stone-600">Total Scans: <span className="font-semibold text-secondary">{stat.count}</span></p>
                      <p className="text-sm text-stone-600">Last Scan: <span className="font-semibold text-secondary">{new Date(stat.lastScan).toLocaleString()}</span></p>
                      <p className="text-sm text-stone-600">Unique Visitors: <span className="font-semibold text-secondary">{stat.uniqueVisitors}</span></p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'menu':
        return <MenuManagement />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className={adminStyles.layout.container}>
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h1 className={adminStyles.typography.heading.h1}>Dashboard</h1>
          <button
            type="button"
            onClick={handleSignOut}
            className={`${adminStyles.components.button.base} ${adminStyles.components.button.danger}`}
          >
            Sign Out
          </button>
        </div>

        {notification && (
          <div
            className={`${adminStyles.components.notification.base} ${
              adminStyles.components.notification[notification.type]
            }`}
            role="status"
          >
            {notification.message}
          </div>
        )}

        {/* Tab Navigation — URL-driven so deep links keep their tab */}
        <div className="flex space-x-1 rounded-xl bg-stone-200/60 p-1 mb-4" role="tablist">
          {([
            { label: 'Overview', value: 'overview' as Tab },
            { label: 'QR Codes', value: 'qrcodes' as Tab },
            { label: 'Menu', value: 'menu' as Tab }
          ]).map((tab) => (
            <button
              key={tab.label}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.value}
              onClick={() => goToTab(tab.value)}
              className={`
                flex-1 rounded-lg py-2 text-sm font-medium leading-5 transition-all duration-200
                ${activeTab === tab.value
                  ? 'bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white shadow-sm'
                  : 'text-stone-600 hover:bg-white hover:shadow-sm'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error banner renders alongside the content so one failed fetch
            can no longer blank out every tab. */}
        {error && (
          <div
            className="p-4 rounded-lg shadow-lg bg-red-500 text-white mb-4 flex items-start justify-between gap-4"
            role="alert"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
              className="shrink-0 w-6 h-6 rounded-full hover:bg-red-600 transition-colors leading-none"
            >
              ✕
            </button>
          </div>
        )}

        {/* Tab Content */}
        <div className="mt-4">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
